/**
 * Build Photo from Version Utility
 * Feature 081: Version-aware comment attachments
 *
 * Constructs a Photo object from version data for rendering attachments
 * (markup, edit suggestions) on the correct photo version.
 */

import type { Photo, ImageVariant } from '../types/photo';
import type { PhotoVersionSnapshot } from '../types/critique';

/**
 * Build a Photo object from version data for attachment rendering.
 *
 * Uses the version's variants instead of the current photo's variants,
 * ensuring attachments render on the correct photo version.
 *
 * @param currentPhoto - The current photo object (provides metadata like title, slug)
 * @param version - The version snapshot with variant URLs
 * @returns A Photo object with version-specific variants
 */
export function buildPhotoFromVersion(
  currentPhoto: Photo,
  version: PhotoVersionSnapshot
): Photo {
  const variants: ImageVariant[] = [];

  // Build variants from version URLs
  if (version.thumbnailUrl) {
    variants.push({
      id: `${version.id}-thumbnail`,
      variantType: 'thumbnail',
      storageUrl: version.thumbnailUrl,
      width: 0, // Thumbnail dimensions not critical for attachment rendering
      height: 0,
      fileSize: 0,
      format: 'jpeg',
      versionId: version.id,
      versionNumber: version.versionNumber,
    });
  }

  if (version.previewUrl) {
    variants.push({
      id: `${version.id}-medium`,
      variantType: 'medium',
      storageUrl: version.previewUrl,
      width: version.width || 0,
      height: version.height || 0,
      fileSize: 0,
      format: 'jpeg',
      versionId: version.id,
      versionNumber: version.versionNumber,
    });
  }

  if (version.fullUrl) {
    variants.push({
      id: `${version.id}-full`,
      variantType: 'full',
      storageUrl: version.fullUrl,
      width: version.width || 0,
      height: version.height || 0,
      fileSize: 0,
      format: 'jpeg',
      versionId: version.id,
      versionNumber: version.versionNumber,
    });
  }

  return {
    ...currentPhoto, // Keep title, slug, userId, etc.
    width: version.width,
    height: version.height,
    variants,
  };
}
